import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import { set } from "lodash";

export type DataSource = "MY_DRIVE" | "TEAM_DRIVE" | "OPEN_FOLDER";

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface TrashPageState {
  source: DataSource;
  trashFromFolderId: string | null;
  data: any[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
  };
  lastFetchTimestamp: number;
    trashSource?: {
    id: string;
    name: string;
    type: string;
  };
}

// Helper functions for sessionStorage
const getDataLoaderSessionState = () => {
  try {
    const savedState = sessionStorage.getItem('dataLoaderState');
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (error) {
    console.error('Failed to parse sessionStorage dataLoaderState:', error);
  }
  return null;
};

const saveDataLoaderSessionState = (state: TrashPageState) => {
  try {
    const stateToSave = {
      source: state.source,
      trashFromFolderId: state.trashFromFolderId,
      trashSource: state.trashSource,
      pagination: state.pagination,
    };
    sessionStorage.setItem('dataLoaderState', JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Failed to save dataLoaderState to sessionStorage:', error);
  }
};

const sessionState = getDataLoaderSessionState();

const initialState: TrashPageState = {
  source: sessionState?.source || "MY_DRIVE", // Default
  trashFromFolderId: sessionState?.trashFromFolderId || null,
  data: [],
  loading: false,
  error: null,
  pagination: sessionState?.pagination || {
    page: 1,
    pageSize: 20,
    totalCount: 0,
  },
  lastFetchTimestamp: 0,
  trashSource: sessionState?.trashSource || {
    id: "my-drive",
    name: "My Drive",
    type: "my-drive",
  },
};

const trashPageSlice = createSlice({
  name: "trashPage",
  initialState,
  reducers: {
    // ✅ Action duy nhất UI cần gọi
    getDataRequest: (
      state,
      action: PayloadAction<{
        source?: DataSource;
        folderId?: string | null;
        page?: number;
        pageSize?: number;
        trashFromFolderId?: string | null;
      }>
    ) => {
      state.loading = true;
      state.error = null;

      // Update source if provided
      if (action.payload.source) {
        state.source = action.payload.source;
      }

      // Update folderId if provided
      if (action.payload.trashFromFolderId !== undefined) {
        state.trashFromFolderId = action.payload.trashFromFolderId;
      }

      // Update pagination
      if (action.payload.page) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.pageSize) {
        state.pagination.pageSize = action.payload.pageSize;
      }
      
    },

    getDataSuccess: (
      state,
      action: PayloadAction<{
        trashSource?: any;
        data: any[];
        totalCount?: number;
      }>
    ) => {
      state.loading = false;
      state.data = action.payload.data;
      state.error = null;
      state.lastFetchTimestamp = Date.now();
      state.trashSource = action.payload.trashSource;

      if (action.payload.totalCount !== undefined) {
        state.pagination.totalCount = action.payload.totalCount;
      }
      
      saveDataLoaderSessionState(state);
    },

    getDataFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.data = [];
      saveDataLoaderSessionState(state);
    },

    // Reset về trạng thái ban đầu
    resetDataLoader: (state) => {
      Object.assign(state, initialState);
      saveDataLoaderSessionState(state);
    },

    // Set source và folderId (không trigger fetch)
    setDataSource: (
      state,
      action: PayloadAction<{
        source: DataSource;
        folderId?: string | null;
        trashSource?: any;
        trashFromFolderId?: string | null;
      }>
    ) => {
      state.source = action.payload.source;
      state.trashSource = action.payload.trashSource;
      if (action.payload.trashFromFolderId !== undefined) {
        state.trashFromFolderId = action.payload.trashFromFolderId;
      }
      saveDataLoaderSessionState(state);
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const trashPageActions = trashPageSlice.actions;
export default trashPageSlice.reducer;

// Selectors
export const selectTrashPageState = (state: RootState) => state.trashPage;
export const selectDataSource = (state: RootState) => state.trashPage.source;
export const selectTrashFromFolderId = (state: RootState) =>
  state.trashPage.trashFromFolderId;
export const selectData = (state: RootState) => state.trashPage.data;
export const selectLoading = (state: RootState) => state.trashPage.loading;
export const selectError = (state: RootState) => state.trashPage.error;
export const selectPagination = (state: RootState) =>
  state.trashPage.pagination;
export const selectTrashSource = (state: any) => state.trashPage.trashSource;